/**
 * @file Rank-reorder sortable context (SE-4685).
 * @description Lets the rank-reorder wrapper (RankSortableCard) hand dnd-kit
 * sortable bindings to the player card it wraps, without prop-drilling a render
 * callback through the column view. When a card renders inside the provider, its
 * own drag box becomes the sortable node (carrying the transform) and it also
 * renders the grab handle inside that same node, so handle and card move
 * together during a reorder. Outside the provider the value is null and the card
 * behaves as the normal cross-column draggable.
 * @module components/organisms/custom/ui/KanbanPlayerCard/rankReorderContext
 */
import { createContext, useContext } from "react";

/**
 * Sortable bindings handed to the player card so its own drag box becomes the
 * dnd-kit sortable node (SE-4685). When present the card body does not register
 * an independent draggable; it carries the sortable transform, transition,
 * activator listeners, and accessibility attributes, and renders the grab
 * handle inside the transformed node.
 */
export interface KanbanCardSortableBindings {
  /** Ref setter for the sortable node (attached to the card's drag box). */
  setNodeRef: (node: HTMLElement | null) => void;
  /** dnd-kit transform for the sortable node, or null when at rest. */
  transform: { x: number; y: number; scaleX: number; scaleY: number } | null;
  /** dnd-kit transition string for smooth reordering. */
  transition?: string;
  /** Whether this card is the one currently being dragged. */
  isDragging: boolean;
  /** dnd-kit accessibility attributes for the sortable node. */
  attributes: Record<string, unknown>;
  /** dnd-kit activator listeners (spread on the whole-row sortable node). */
  listeners?: Record<string, unknown>;
}

/**
 * Context carrying the rank-reorder sortable bindings for the card rendered
 * within a RankSortableCard, or null outside it.
 */
export const RankReorderCardContext =
  createContext<KanbanCardSortableBindings | null>(null);

/**
 * Read the rank-reorder sortable bindings for the current card.
 * @returns The sortable bindings, or null when not inside a RankSortableCard.
 */
export const useRankReorderSortable = (): KanbanCardSortableBindings | null =>
  useContext(RankReorderCardContext);
